import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/Form/Input';
import { EntityEditLayout } from '../../components';
import { useSupplier, useSaveSupplier } from '../../hooks/useSuppliers';
import { useCountries, useRegions } from '../../hooks/useReferenceData';

interface Country {
  id: number;
  name: string;
  code: string;
}

interface Region {
  id: number;
  name: string;
  countryId: number;
}

interface SupplierFormData {
  name: string;
  address: string;
  countryId: string;
  regionId: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  website: string;
  description: string;
}

export const SupplierEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  const { data: countriesData } = useCountries();
  const { data: regionsData } = useRegions();
  const { data: supplierData, isLoading: isSupplierLoading } = useSupplier(
    id as string,
    isEditMode && Boolean(id),
  );
  const saveSupplierMutation = useSaveSupplier();
  const countries = (countriesData || []) as Country[];
  const regions = (regionsData || []) as Region[];
  const [filteredRegions, setFilteredRegions] = useState<Region[]>([]);
  
  const [formData, setFormData] = useState<SupplierFormData>({
    name: '',
    address: '',
    countryId: '',
    regionId: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    website: '',
    description: '',
  });

  const [errors, setErrors] = useState<Partial<SupplierFormData>>({});

  useEffect(() => {
    if (isEditMode && supplierData) {
      setFormData({
        name: supplierData.name || '',
        address: supplierData.address || '',
        countryId: supplierData.countryId ? String(supplierData.countryId) : '',
        regionId: supplierData.regionId ? String(supplierData.regionId) : '',
        contactName: supplierData.contactName || '',
        contactEmail: supplierData.contactEmail || '',
        contactPhone: supplierData.contactPhone || '',
        website: supplierData.website || '',
        description: supplierData.description || '',
      });
    }
  }, [isEditMode, supplierData]);

  useEffect(() => {
    if (formData.countryId) {
      const filtered = regions.filter(r => Number(r.countryId) === parseInt(formData.countryId));
      setFilteredRegions(filtered);
      
      // Reset region if it doesn't belong to selected country
      if (formData.regionId && !filtered.find(r => r.id === parseInt(formData.regionId))) {
        setFormData(prev => ({ ...prev, regionId: '' }));
      }
    } else {
      setFilteredRegions([]);
    }
  }, [formData.countryId, regions]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error for this field
    if (errors[name as keyof SupplierFormData]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const validate = (): boolean => {
    const newErrors: Partial<SupplierFormData> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Supplier name is required';
    }

    if (formData.contactEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contactEmail)) {
      newErrors.contactEmail = 'Invalid email format';
    }

    if (formData.website && !/^https?:\/\/.+/.test(formData.website)) {
      newErrors.website = 'Website must start with http:// or https://';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    try {
      const payload = {
        name: formData.name,
        address: formData.address || null,
        countryId: formData.countryId ? parseInt(formData.countryId) : null,
        regionId: formData.regionId ? parseInt(formData.regionId) : null,
        contactName: formData.contactName || null,
        contactEmail: formData.contactEmail || null,
        contactPhone: formData.contactPhone || null,
        website: formData.website || null,
        description: formData.description || null,
      };

      const result = await saveSupplierMutation.mutateAsync({
        id: isEditMode ? (id as string) : null,
        payload,
      });
      
      // Navigate to the supplier detail page
      navigate(`/suppliers/${result.id}`);
    } catch (error) {
      console.error('Error saving supplier:', error);
      alert('Failed to save supplier. Please try again.');
    }
  };

  return (
    <EntityEditLayout
      backTo={isEditMode ? `/suppliers/${id}` : '/suppliers'}
      backLabel={isEditMode ? 'Back to Supplier' : 'Back to Suppliers'}
      title={isEditMode ? 'Edit Supplier' : 'Create New Supplier'}
      isLoading={isSupplierLoading}
    >
        <form onSubmit={handleSubmit}>
          {/* Basic Information Card */}
          <div className="mb-6">
            <div style={{
              display: 'flex',
              width: '733px',
              minHeight: '371px',
              flexDirection: 'column',
              alignItems: 'stretch',
              flexShrink: 0,
              backgroundColor: 'white',
              borderRadius: '8px',
              border: '1px solid #EAECF0',
              padding: '24px'
            }}>
              <h2 style={{
                fontFamily: 'Oxygen',
                fontSize: '18px',
                fontWeight: 700,
                color: '#101828',
                marginBottom: '24px'
              }}>
                Basic Information
              </h2>

            <div className="space-y-6">
              {/* Supplier Name */}
              <div>
                <label htmlFor="name" style={{
                  fontFamily: 'Oxygen',
                  fontSize: '14px',
                  fontWeight: 700,
                  color: '#344054',
                  display: 'block',
                  marginBottom: '6px'
                }}>
                  Supplier Name <span style={{ color: '#DC2626' }}>*</span>
                </label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter supplier name"
                  error={errors.name}
                />
              </div>

              {/* Physical Address */}
              <div>
                <label htmlFor="address" style={{
                  fontFamily: 'Oxygen',
                  fontSize: '14px',
                  fontWeight: 700,
                  color: '#344054',
                  display: 'block',
                  marginBottom: '6px'
                }}>
                  Physical Address
                </label>
                <Input
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="Enter physical address"
                />
              </div>

              {/* Country and Region on same line */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="countryId" style={{
                    fontFamily: 'Oxygen',
                    fontSize: '14px',
                    fontWeight: 700,
                    color: '#344054',
                    display: 'block',
                    marginBottom: '6px'
                  }}>
                    Country
                  </label>
                  <select
                    id="countryId"
                    name="countryId"
                    value={formData.countryId}
                    onChange={handleChange}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      fontFamily: 'Oxygen',
                      fontSize: '16px',
                      border: '1px solid #D0D5DD',
                      borderRadius: '8px',
                      backgroundColor: 'white',
                      color: '#101828'
                    }}
                  >
                    <option value="">Select a country</option>
                    {countries.map(country => (
                      <option key={country.id} value={country.id}>
                        {country.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="regionId" style={{
                    fontFamily: 'Oxygen',
                    fontSize: '14px',
                    fontWeight: 700,
                    color: '#344054',
                    display: 'block',
                    marginBottom: '6px'
                  }}>
                    Region
                  </label>
                  <select
                    id="regionId"
                    name="regionId"
                    value={formData.regionId}
                    onChange={handleChange}
                    disabled={!formData.countryId}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      fontFamily: 'Oxygen',
                      fontSize: '16px',
                      border: '1px solid #D0D5DD',
                      borderRadius: '8px',
                      backgroundColor: formData.countryId ? 'white' : '#F9FAFB',
                      color: '#101828',
                      cursor: formData.countryId ? 'pointer' : 'not-allowed'
                    }}
                  >
                    <option value="">Select a region</option>
                    {filteredRegions.map(region => (
                      <option key={region.id} value={region.id}>
                        {region.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
          </div>

          {/* Contact Information Card */}
          <div>
            <div style={{
            display: 'flex',
            width: '733px',
            minHeight: '371px',
            flexDirection: 'column',
            alignItems: 'stretch',
            flexShrink: 0,
            backgroundColor: 'white',
            borderRadius: '8px',
            border: '1px solid #EAECF0',
            padding: '24px'
          }}>
            <h2 style={{
              fontFamily: 'Oxygen',
              fontSize: '18px',
              fontWeight: 700,
              color: '#101828',
              marginBottom: '24px'
            }}>
              Contact Information
            </h2>

            <div className="space-y-6">
              {/* Supplier Contact Name */}
              <div>
                <label htmlFor="contactName" style={{
                  fontFamily: 'Oxygen',
                  fontSize: '14px',
                  fontWeight: 700,
                  color: '#344054',
                  display: 'block',
                  marginBottom: '6px'
                }}>
                  Supplier Contact Name
                </label>
                <Input
                  id="contactName"
                  name="contactName"
                  value={formData.contactName}
                  onChange={handleChange}
                  placeholder="Enter contact person name"
                />
              </div>

              {/* Website */}
              <div>
                <label htmlFor="website" style={{
                  fontFamily: 'Oxygen',
                  fontSize: '14px',
                  fontWeight: 700,
                  color: '#344054',
                  display: 'block',
                  marginBottom: '6px'
                }}>
                  Website
                </label>
                <Input
                  id="website"
                  name="website"
                  type="url"
                  value={formData.website}
                  onChange={handleChange}
                  placeholder="https://www.example.com"
                  error={errors.website}
                />
              </div>

              {/* Email Address */}
              <div>
                <label htmlFor="contactEmail" style={{
                  fontFamily: 'Oxygen',
                  fontSize: '14px',
                  fontWeight: 700,
                  color: '#344054',
                  display: 'block',
                  marginBottom: '6px'
                }}>
                  Email Address
                </label>
                <Input
                  id="contactEmail"
                  name="contactEmail"
                  type="email"
                  value={formData.contactEmail}
                  onChange={handleChange}
                  placeholder="contact@example.com"
                  error={errors.contactEmail}
                />
              </div>

              {/* Phone Number */}
              <div>
                <label htmlFor="contactPhone" style={{
                  fontFamily: 'Oxygen',
                  fontSize: '14px',
                  fontWeight: 700,
                  color: '#344054',
                  display: 'block',
                  marginBottom: '6px'
                }}>
                  Phone Number
                </label>
                <Input
                  id="contactPhone"
                  name="contactPhone"
                  type="tel"
                  value={formData.contactPhone}
                  onChange={handleChange}
                  placeholder="+61 8 1234 5678"
                />
              </div>
            </div>
          </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 mt-8 max-w-[733px]">
            <Button
               type="button"
               variant="ghost"
               onClick={() => navigate(isEditMode ? `/suppliers/${id}` : '/suppliers')}
               disabled={saveSupplierMutation.isPending}
             >
               Cancel
             </Button>
             <Button
               type="submit"
               variant="primary"
               disabled={saveSupplierMutation.isPending}
             >
               {saveSupplierMutation.isPending
                 ? 'Saving...'
                 : isEditMode
                   ? 'Save Changes'
                   : 'Create Supplier'}
             </Button>
          </div>
        </form>
    </EntityEditLayout>
  );
};

export default SupplierEditPage;
